// BHP-1083: domain detail on Backend2.0 (`GET /api/domains/{uuid}`), Tailwind.
// The hardcoded contact-demo panel is removed. The DNS section (DomainSettings →
// DnsZoneOverview) is a separate feature and stays out of scope.
//
// BHP-1099: the uuid comes from the URL and is handed down to the DNS/database
// panels as a prop. No selection store, no localStorage — a refresh, a bookmark
// and a second tab all just work.
import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import DomainSettings from '../../components/Domain/DomainSettings';
import { useDomain } from '../../features/domain/application/domainQueries';
import { useDeleteDomain } from '../../features/domain/application/domainMutations';
import { ApiError } from '../../shared/http/ApiError';
import { toUserMessage } from '../../shared/http/errorMessage';
import InlineConfirm from '../../shared/ui/InlineConfirm';

function InfoRow({
  label,
  value
}: {
  label: string;
  value: string;
}): JSX.Element {
  return (
    <div className="flex justify-between gap-3 border-0 border-b border-solid border-slate-100 px-4 py-2 text-sm last:border-b-0">
      <dt className="text-slate-400">{label}</dt>
      <dd className="font-medium text-slate-700">{value}</dd>
    </div>
  );
}

/** A uuid the active customer cannot reach resolves to nothing, not to an error. */
function isOutOfScope(error: unknown): boolean {
  return error instanceof ApiError && [403, 404].includes(error.status);
}

export default function DomainDetails(): JSX.Element {
  const navigate = useNavigate();
  const { uuid = null } = useParams<{ uuid: string }>();

  const { data: domain, isPending, isError, error } = useDomain(uuid);
  const { remove, isPending: isDeleting } = useDeleteDomain();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const onDelete = async (): Promise<void> => {
    if (uuid === null) {
      return;
    }
    setDeleteError(null);
    try {
      await remove(uuid);
    } catch (caught) {
      // Without this the failure is invisible: the button re-enables and the
      // domain is still there.
      setDeleteError(toUserMessage(caught, 'Could not cancel this domain.'));
      return;
    }
    navigate('/domain/dashboard');
  };

  // Stale bookmark, or a domain belonging to a customer you are no longer
  // acting for: nothing to show and nothing broken, so go to the overview.
  if (isOutOfScope(error)) {
    return <Navigate to="/domain/dashboard" replace />;
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {isPending ? (
        <p className="text-sm text-slate-500">Loading domain…</p>
      ) : isError || domain === undefined ? (
        <div
          role="alert"
          className="rounded-lg border border-solid border-rose-200 bg-rose-50 p-4 text-sm text-rose-700"
        >
          {toUserMessage(error, 'Could not load this domain.')}
        </div>
      ) : (
        <>
          <h2 className="text-lg font-semibold text-slate-900">
            {domain.domainName}
            {domain.tld !== null ? `.${domain.tld}` : ''}
          </h2>

          <dl className="overflow-hidden rounded-xl border border-solid border-slate-200 bg-white">
            <InfoRow
              label="Registration status"
              value={domain.registrationStatus}
            />
            {domain.registrationFailureReason !== null ? (
              <InfoRow
                label="Failure reason"
                value={domain.registrationFailureReason}
              />
            ) : null}
            <InfoRow label="Renewal date" value={domain.renewalDate ?? '—'} />
            <InfoRow label="Created" value={domain.createdOn ?? '—'} />
          </dl>

          {/* BHP-1063: DNS on Backend2.0; the Databases tab is still legacy. */}
          <DomainSettings
            domainUuid={domain.uuid}
            dnsZoneUuid={domain.dnsZoneUuid}
          />

          <div className="rounded-xl border border-solid border-rose-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-rose-700">
              Cancel this domain
            </h3>
            {/* Matches the backend: DELETE turns auto-renew off and schedules
                teardown at expiry — it is not an immediate deletion. */}
            <p className="mt-1 text-xs text-slate-500">
              Auto-renewal is switched off and the domain keeps working until
              its expiry date, after which it is released. This cannot be
              reversed.
            </p>
            {deleteError !== null ? (
              <p
                role="alert"
                className="mt-3 rounded border border-solid border-rose-200 bg-rose-50 p-2 text-sm text-rose-700"
              >
                {deleteError}
              </p>
            ) : null}
            {confirmingDelete ? (
              <InlineConfirm
                className="mt-3"
                prompt={
                  <p className="m-0 text-sm text-slate-700">
                    Cancel{' '}
                    <strong>
                      {domain.domainName}
                      {domain.tld !== null ? `.${domain.tld}` : ''}
                    </strong>
                    ?
                  </p>
                }
                confirmLabel="Confirm cancellation"
                pendingLabel="Cancelling…"
                cancelLabel="Keep domain"
                isPending={isDeleting}
                onConfirm={() => {
                  void onDelete();
                }}
                onCancel={() => {
                  setConfirmingDelete(false);
                }}
              />
            ) : (
              <button
                type="button"
                onClick={() => {
                  setConfirmingDelete(true);
                }}
                className="mt-3 rounded-md border-0 bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700"
              >
                Cancel domain
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
